import { z } from 'zod';

import { META_PIXEL_ID_PATTERN, normalizeSocialUrl, type SocialNetwork } from './social-links';

/**
 * Mirrors apps/api/src/modules/store/http/store.dto.ts's
 * `storeConfigurationSchema` field-for-field. `PUT` is a full replace
 * (store.dto.ts's own header: "every optional field omitted is cleared") —
 * `StoreForm` always submits the complete representation it last read via
 * `GET`, never a partial diff.
 *
 * `themeId`/`themeColors` are deliberately NOT exposed in this form: no
 * theme-selection UI exists anywhere in this project yet, and building one
 * was not asked for — omitting them from the submitted body clears
 * whatever the field held (`store.dto.ts`'s own PUT semantics), which is
 * correct for a merchant who has never set a theme and would silently
 * erase one for a merchant who somehow has (nobody does, today — no UI
 * writes it). Flagged in docs/resumée.txt rather than silently decided.
 */

const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

const SLUG_MESSAGE =
  'Le slug doit être en minuscules, chiffres et tirets simples, 2 à 63 caractères.';

const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, SLUG_MESSAGE)
  .max(63, SLUG_MESSAGE)
  .regex(SLUG_PATTERN, SLUG_MESSAGE);

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Identité visuelle par marchand lot — a native `<input type="color">`
 * can never represent "no value" (the browser always reports SOME hex,
 * defaulting to black), so this project's own "not set" state is
 * represented in the FORM as a literal `null`, only ever written there by
 * the "Réinitialiser" control next to each swatch in `StoreForm.tsx` —
 * never by the color input itself.
 */
const optionalHexColor = z
  .string()
  .trim()
  .regex(HEX_COLOR_PATTERN, 'La couleur doit être un code hexadécimal à 6 chiffres, ex. #1E6E76.')
  .nullable()
  .optional()
  .transform((value) => (value === undefined ? null : value));

/** Same empty-string-to-null normalisation for every other optional text field — a blank input must mean "not set", not a validation failure. */
const optionalText = (
  maxLength: number,
): z.ZodType<string | null, z.ZodTypeDef, string | null | undefined> =>
  z
    .string()
    .trim()
    .max(maxLength)
    .nullable()
    .optional()
    .transform((value) => (value === '' || value === undefined ? null : value));

const WHATSAPP_PATTERN = /^[1-9][0-9]{7,14}$/;
const TUNISIA_COUNTRY_CODE = '216';

/**
 * Mirrors `normalizeWhatsappNumber` in apps/api/src/modules/store/http/store.dto.ts
 * exactly (same rule, enforced independently on both sides): whatever the
 * merchant typed ("+216 22 123 456", "22 123 456") becomes digits-only
 * international form, or `null` if it cannot be a real number. A bare
 * 8-digit number not starting with 0 is read as a Tunisian local number.
 */
export function normalizeWhatsappNumber(raw: string): string | null {
  const compact = raw.replace(/[\s().-]/g, '');
  let digits = compact;
  if (compact.startsWith('+')) digits = compact.slice(1);
  else if (compact.startsWith('00')) digits = compact.slice(2);

  if (!/^[0-9]+$/.test(digits)) return null;

  const international =
    digits.length === 8 && !digits.startsWith('0') ? `${TUNISIA_COUNTRY_CODE}${digits}` : digits;
  return WHATSAPP_PATTERN.test(international) ? international : null;
}

/** Blank means "not set" (null); anything else must normalise to a real number, and is submitted in its normalised digits-only form. */
const optionalWhatsapp = z
  .string()
  .trim()
  .max(40)
  .nullable()
  .optional()
  .transform((value, ctx): string | null => {
    if (value === '' || value === undefined || value === null) return null;
    const normalized = normalizeWhatsappNumber(value);
    if (normalized === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Numéro WhatsApp invalide. Entrez-le avec l’indicatif, ex. +216 22 123 456.',
      });
      return z.NEVER;
    }
    return normalized;
  });

/** Blank means "not set"; anything else must be an https link to that network, submitted in its normalised form. */
function optionalSocialUrl(
  network: SocialNetwork,
): z.ZodType<string | null, z.ZodTypeDef, string | null | undefined> {
  return z
    .string()
    .trim()
    .max(600)
    .nullable()
    .optional()
    .transform((value, ctx): string | null => {
      if (value === '' || value === undefined || value === null) return null;
      const normalized = normalizeSocialUrl(network, value);
      if (normalized === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lien invalide : collez l’adresse https:// de votre page sur ce réseau.',
        });
        return z.NEVER;
      }
      return normalized;
    });
}

const optionalMetaPixelId = z
  .string()
  .trim()
  .max(40)
  .nullable()
  .optional()
  .transform((value) => (value === '' || value === undefined ? null : value))
  .refine((value) => value === null || META_PIXEL_ID_PATTERN.test(value), {
    message: 'Le Pixel ID Meta est un nombre de 10 à 20 chiffres (Gestionnaire d’événements Meta).',
  });

export const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** One free-text field per day (e.g. "09:00–18:00", "Fermé") — matches exactly the keys apps/storefront's own OPENING_HOURS_LABELS already reads. */
export const openingHoursFormSchema = z.object(
  Object.fromEntries(DAY_KEYS.map((day) => [day, z.string().trim().max(50)])) as Record<
    (typeof DAY_KEYS)[number],
    z.ZodString
  >,
);

export const storeFormSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1, 'Le nom est requis.').max(200),

  description: optionalText(5000),
  /** Lot 1 / 1A — one-line slogan shown in the storefront hero and footer (160 max, migration 0063). */
  tagline: optionalText(160),
  address: optionalText(500),
  phone: optionalText(30),
  /** Lot 1 / 1A — submitted as digits-only international form (see `normalizeWhatsappNumber`). */
  whatsapp: optionalWhatsapp,
  /** Lot 1 / 1B — the « Notre histoire » block: an optional heading (120 max) and the story itself (2000 max), PLAIN TEXT — paragraphs are separated by a blank line and the storefront never interprets markup (migration 0064). */
  aboutTitle: optionalText(120),
  aboutBody: optionalText(2000),
  email: z
    .union([z.literal(''), z.string().trim().email()])
    .nullable()
    .optional()
    .transform((value) => (value === '' || value === undefined ? null : value)),

  openingHours: openingHoursFormSchema,

  returnPolicy: optionalText(20_000),
  shippingPolicy: optionalText(20_000),
  termsOfService: optionalText(20_000),
  privacyPolicy: optionalText(20_000),

  seoTitle: optionalText(200),
  seoDescription: optionalText(500),

  stockDisplayPolicy: z.enum(['exact', 'low_stock_only', 'hidden']),

  /** TND. 0 (the default) means no delivery fee is charged — mirrors `stores.delivery_fee`'s own `DEFAULT 0`. Registered with `valueAsNumber: true` in `StoreForm.tsx`, the same pattern `ProductForm.tsx`'s own `price` field already uses. */
  deliveryFee: z.number().nonnegative().max(999_999),

  /**
   * Identité visuelle par marchand lot — `logoUrl`/`bannerUrl` are
   * deliberately ABSENT here (Upload réel du logo/bannière lot): a
   * merchant configures either through `useUploadStoreBrandingMutation`
   * (a real file upload), never a pasted URL field on this form. The 8
   * colors are all `null` by default (never a fabricated hex) — `null`
   * means "inherit the platform's current token", resolved
   * storefront-side by `resolveShopThemeStyle()`, never decided here.
   */
  colorPrimary: optionalHexColor,
  colorSecondary: optionalHexColor,
  colorAccent: optionalHexColor,
  colorText: optionalHexColor,
  colorButton: optionalHexColor,
  colorBadge: optionalHexColor,
  colorPromo: optionalHexColor,
  colorVariant: optionalHexColor,

  /** Migration 0096 — Meta Pixel ID and social links (`social-links.ts`). */
  metaPixelId: optionalMetaPixelId,
  socialFacebook: optionalSocialUrl('facebook'),
  socialInstagram: optionalSocialUrl('instagram'),
  socialTiktok: optionalSocialUrl('tiktok'),
  socialYoutube: optionalSocialUrl('youtube'),
  socialX: optionalSocialUrl('x'),
  socialLinkedin: optionalSocialUrl('linkedin'),
});
export type StoreFormValues = z.infer<typeof storeFormSchema>;
